import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, FormArray } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { DeviceInfo } from 'ngx-device-detector/public-api';
import { firstValueFrom, Subscription } from 'rxjs';
import { AddToProductComponent } from 'src/app/barcode/add-to-product/add-to-product.component';
import { CartService } from 'src/app/cart/cart.service';
import { UploadComponent } from 'src/app/provider-price/upload/upload.component';
import { addToCart, removeFromCart, updateProductInCart } from 'src/app/redux/actions/cart.actions';
import { AppState } from 'src/app/redux/app.state';
import { NotificationService } from 'src/app/services/notification.service';
import { SupportService } from 'src/app/support.service';
import { environment } from 'src/environments/environment';
import { ComputerService } from 'src/app/computer/computer.service';
import { LabelPrintComponent } from 'src/app/computer/label-print/label-print.component';
import {
  LabelProfileRef,
  Product,
  ProductImportFromExistCandidate,
  ProductImportFromExistResult,
  ProductInCart,
  ProductWithControls,
  ProductWithControlsC,
  ProductCellBalance,
  ProductStockSummary,
  User,
  Warehouse
} from 'src/typings';
import { AddImageComponent } from '../add-image/add-image.component';
import { AnalogueListComponent } from '../analogue-list/analogue-list.component';
import { EditStockComponent } from '../edit-stock/edit-stock.component';
import { FoldersComponent } from '../folders/folders.component';
import { PropertiesComponent } from '../info/properties/properties.component';
import { MoveComponent } from '../move/move.component';
import { NameUpdateComponent } from '../name-update/name-update.component';
import { ProductService } from '../product.service';
import { RemoveDoubleComponent } from '../remove-double/remove-double.component';
import { SetNewPriceComponent } from '../set-new-price/set-new-price.component';
import { CatalogStockDialogComponent } from '../catalog-stock-dialog/catalog-stock-dialog.component';
import { ExistImportSelectionDialogComponent } from '../exist-import-selection-dialog/exist-import-selection-dialog.component';
@Component({
  selector: 'app-product-table',
  templateUrl: './product-table.component.html',
  styleUrls: ['./product-table.component.scss']
})
export class ProductTableComponent implements OnDestroy {
  private readonly bufferLegacyLabel = 'Буфер размещения';

  @Input() set products(payload: Product[]) {
    this._products = payload;
  }

  @Input() productsFormArray: FormArray | undefined;

  @Input() selectedProductsFormArray: FormArray | undefined;

  @Input() loadingProducts: Product[] = [];

  _products: Product[] = [];

  deviceInfo: DeviceInfo | null = null;

  get products() {
    if (this.productsFormArray !== undefined) {
      return this.productsFormArray.value.map((row: ProductWithControls) => row.product);
    } else {
      return this._products;
    }
  }

  @Input() updateViewEvent: EventEmitter<void> = new EventEmitter();

  @Output() sortEvent: EventEmitter<any> = new EventEmitter();

  @Output() rowChangeEvent: EventEmitter<ProductWithControls> = new EventEmitter();

  @Output() removeEvent: EventEmitter<Product> = new EventEmitter();

  @Input() loading: boolean = false;

  @Input() set columnsToDisplay(payload: string[]) {
    this._columnsToDisplay = payload;
  }

  @ViewChild('table') table: any;

  get columnsToDisplay() {
    return this._columnsToDisplay;
  }

  private _columnsToDisplay = [
    'id',
    'image',
    'description',
    'brand',
    'article',
    'analogies',
    'retail',
    'stock',
    'cell',
    'selector'
  ];

  @Output() selectEvent: EventEmitter<Product> = new EventEmitter();

  @Output() unSelectEvent: EventEmitter<Product> = new EventEmitter();

  user: User | undefined;

  storeSubscription: Subscription;

  @ViewChild(MatMenuTrigger, { static: true }) matMenuTrigger: MatMenuTrigger | undefined;

  menuTopLeftPosition = { x: '0', y: '0' };

  cartProductsFrom: FormArray = this.fb.array([]);

  @Input() mainWarehouse: Warehouse | undefined;

  set cartProducts(products: ProductInCart[]) {
    this._cartProducts = products;
    this.cartProductsFrom.clear();
    products.forEach((prod) => {
      this.cartProductsFrom.push(this.fb.group(prod));
    });
  }

  getMainStock(product: Product) {
    if (!this.mainWarehouse) return { quan: 0, cell: '' };
    const find = product.stock.find((row) => row.warehouse.id === this.mainWarehouse?.id);
    return find;
  }

  _cartProducts: ProductInCart[] = [];

  productSubscription: Subscription;

  constructor(
    private productService: ProductService,
    public supportService: SupportService,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private cartService: CartService,
    private fb: FormBuilder,
    private titleService: Title,
    private computerService: ComputerService,
    private router: Router
  ) {
    this.storeSubscription = this.store.subscribe((data) => {
      this.user = data.user.info;
      this.deviceInfo = data.user.deviceInfo;
      this.cartProducts = data.cart.map((prod: any) => {
        return { ...prod, quan: Number(prod.quan) };
      });
    });
    this.productSubscription = this.productService.productGw.subscribe((list) => {
      this.updateProducts(list);
    });
  }

  loadingProm: { id: number }[] = [];

  loadingAvtopro: { id: number }[] = [];

  loadingPublic: { id: number }[] = [];

  loadingUpdateRetail: { id: number }[] = [];

  importingFromExistIds: number[] = [];

  isLoading(product: Product) {
    const findIndex = this.loadingProducts.findIndex((prod) => prod.id === product.id);
    return findIndex > -1;
  }

  get isAdmin() {
    return this.user?.roles?.some((role) => role.description === 'admin') === true;
  }

  ngOnDestroy(): void {
    this.productSubscription.unsubscribe();
    this.storeSubscription.unsubscribe();
  }

  isSelected(product: Product) {
    const index = this.getIndexOfSelected(product);
    return index > -1;
  }

  getIndexOfSelected(product: Product): number {
    if (!this.selectedProductsFormArray) return -1;
    const findIndex = this.selectedProductsFormArray.value.findIndex((row: ProductWithControls) => {
      return row.product.id === product.id;
    });

    return findIndex;
  }

  removeProduct(index: number) {
    if (!this.selectedProductsFormArray) return;
    return this.selectedProductsFormArray.removeAt(index);
  }

  addProduct(product: Product) {
    if (!this.selectedProductsFormArray) return;
    if (this.productsFormArray && this.selectedProductsFormArray !== this.productsFormArray) {
      return this.selectedProductsFormArray.push(
        this.fb.group({
          product: new FormControl(product)
        })
      );
    }

    const control = this.getProductGroupControl(product);
    if (!control) return;
    return this.selectedProductsFormArray.push(control);
  }

  toggleProduct(product: Product) {
    if (this.isLoading(product)) return;
    const index = this.getIndexOfSelected(product);
    if (index !== -1) {
      return this.removeProduct(index);
    }
    return this.addProduct(product);
  }

  handleControlChanges(group: FormGroup<ProductWithControlsC> | null) {
    if (!group) return;
    this.rowChangeEvent.emit(group.value as ProductWithControls);
  }

  getProductGroupControl(product: Product): FormGroup<ProductWithControlsC> | null {
    if (!this.productsFormArray) return new FormGroup({ product: new FormControl(product) });
    const findIndex = this.productsFormArray.controls.findIndex((row) => {
      return row.value.product.id === product.id;
    });
    if (findIndex !== -1) {
      return this.productsFormArray.controls[findIndex] as FormGroup<ProductWithControlsC>;
    }
    return null;
  }

  getRetailControl(group: FormGroup<ProductWithControlsC> | null): FormControl {
    if (group && group.controls.retail) return group.controls.retail as FormControl<number>;
    return new FormControl();
  }

  getQuanControl(group: FormGroup<ProductWithControlsC> | null): FormControl {
    const result = group && group.controls.quan ? group.controls.quan : new FormControl();
    return result;
  }

  getCellControl(group: FormGroup<ProductWithControlsC> | null): FormControl {
    if (group && group.controls.cell) return group.controls.cell as FormControl<string>;
    return new FormControl();
  }

  getPriceControl(group: FormGroup<ProductWithControlsC>): FormControl {
    return group.controls.price as FormControl<number>;
  }

  updateProducts(list: Product[]) {
    if (!list) return;
    if (this.productsFormArray !== undefined) return this.updateProductsFormArray(list);
    const updatedList = [...this.products];
    let changed = false;
    for (let index = 0; index < list.length; index++) {
      const product = list[index];
      const foundIndex = updatedList.findIndex((prod) => prod.id === product.id);
      if (foundIndex < 0) continue;
      updatedList[foundIndex] = {
        ...updatedList[foundIndex],
        ...product
      };
      changed = true;
    }
    if (changed) this.products = updatedList;
  }

  updateProductsFormArray(list: Product[]) {
    if (!list || !this.productsFormArray) return;
    for (let index = 0; index < list.length; index++) {
      const product = list[index];
      const find = this.productsFormArray.controls.find(
        (row) => row.value.product.id === product.id
      ) as FormGroup<ProductWithControlsC> | undefined;
      if (!find) continue;
      find.controls.product.setValue(product);
    }
  }

  remove(product: Product) {
    this.removeEvent.emit(product);
  }

  getImg(product: Product) {
    if (!product.images || product.images.length === 0) return null;
    const img = product.images[0].name;
    return `${environment.BACKEND}/image/${img}`;
  }

  getWarehouse(product: Product) {
    const warehouse = product.stock.find((wh) => {
      return wh.warehouse.id === this.user?.defaultWarehouse.id;
    });
    return warehouse;
  }

  getWarehouseSummary(product: Product): ProductStockSummary | undefined {
    const warehouseId = this.user?.defaultWarehouse?.id;
    if (!warehouseId) return undefined;
    return product.stockSummary?.[String(warehouseId)];
  }

  getWarehouseCellBalances(product: Product): ProductCellBalance[] {
    const warehouseId = this.user?.defaultWarehouse?.id;
    if (!warehouseId) return [];
    return (product.cellBalances || []).filter((row) => row.warehouseId === warehouseId);
  }

  getRealCellBalances(product: Product) {
    return this.getWarehouseCellBalances(product).filter(
      (row) => row.type === 'REAL' && row.qty > 0
    );
  }

  getRecommendedCells(product: Product) {
    return this.getWarehouseSummary(product)?.recommendedCells || [];
  }

  getAddressLabel(product: Product) {
    const recommendedCells = this.getRecommendedCells(product);
    if (recommendedCells.length) {
      return recommendedCells.join(', ');
    }
    return this.formatLegacyCellLabel(this.getWarehouse(product)?.cell);
  }

  hasAddressDebt(product: Product) {
    return (this.getWarehouseSummary(product)?.unassignedOutQty || 0) < 0;
  }

  hasBufferStock(product: Product) {
    return (this.getWarehouseSummary(product)?.bufferQty || 0) > 0;
  }

  hasNoPlacedStock(product: Product) {
    const summary = this.getWarehouseSummary(product);
    if (!summary) return false;
    return summary.aggregateQty > 0 && summary.placedQty <= 0;
  }

  hasMultiCellPlacement(product: Product) {
    return this.getRealCellBalances(product).length > 1;
  }

  getAddressBadgeList(product: Product) {
    const badges: string[] = [];
    const recommendedCells = this.getRecommendedCells(product);

    if (recommendedCells.length) {
      badges.push(`Полки: ${recommendedCells.join(', ')}`);
    }
    if (this.hasAddressDebt(product)) {
      badges.push('Адресный долг');
    }
    if (this.hasBufferStock(product)) {
      badges.push('Есть в буфере');
    }
    if (this.hasNoPlacedStock(product)) {
      badges.push('Без размещения');
    }
    if (this.hasMultiCellPlacement(product)) {
      badges.push('Несколько полок');
    }

    return badges;
  }

  private formatLegacyCellLabel(cell?: string | null) {
    return String(cell || '').replace(/\bBUFFER\b/g, this.bufferLegacyLabel);
  }

  openScanner(product: Product, mode: 'PLACE' | 'PICK' | 'COUNT' = 'PICK') {
    const warehouseId = this.user?.defaultWarehouse?.id;
    void this.router.navigate(['/warehouse/scanner'], {
      queryParams: {
        warehouse_id: warehouseId,
        product_id: product.id,
        mode
      }
    });
  }

  openStockDetails(product: Product) {
    const dialogRef = this.dialog.open(CatalogStockDialogComponent, {
      width: '720px',
      maxWidth: '95vw'
    });
    dialogRef.componentInstance.product = product;
    dialogRef.componentInstance.warehouse = this.user?.defaultWarehouse;
  }

  onRightClick(event: MouseEvent, item: Product) {
    if (!this.matMenuTrigger) return;
    // preventDefault avoids to show the visualization of the right-click menu of the browser
    event.preventDefault();

    // we record the mouse position in our object
    this.menuTopLeftPosition.x = event.clientX + 'px';
    this.menuTopLeftPosition.y = event.clientY + 'px';

    // we open the menu
    // we pass to the menu the information about our object
    this.matMenuTrigger.menuData = { item: item };

    // we open the menu
    this.matMenuTrigger.openMenu();
  }

  removeDouble(product: Product) {
    const dialog = this.dialog.open(RemoveDoubleComponent);
    dialog.componentInstance.product = product;
    dialog.componentInstance.doneEvent.subscribe((data) => {
      if (data === true) dialog.close();
    });
  }

  addImage(product: Product) {
    const dialog = this.dialog.open(AddImageComponent);
    dialog.componentInstance.product = product;
    dialog.componentInstance.doneEvent.subscribe((data) => {
      if (data) dialog.close();
    });
  }

  async importFromExist(product: Product) {
    if (!product.article?.trim()) {
      this.notificationService.warning('У товара нет артикула для поиска на Exist');
      return;
    }

    if (this.isImportingFromExist(product.id)) {
      return;
    }

    this.importingFromExistIds = [...this.importingFromExistIds, product.id];
    try {
      await this.runExistImport(product);
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось импортировать товар с Exist');
    } finally {
      this.importingFromExistIds = this.importingFromExistIds.filter((id) => id !== product.id);
    }
  }

  isImportingFromExist(productId: number) {
    return this.importingFromExistIds.includes(productId);
  }

  private async runExistImport(product: Product, selectedExistProductId?: number): Promise<void> {
    const response = await firstValueFrom(
      this.productService.importFromExist(product.id, {
        selected_exist_product_id: selectedExistProductId
      })
    );

    if (response.status === 'not_found') {
      this.notificationService.warning('На Exist ничего не найдено по этому артикулу');
      return;
    }

    if (response.status === 'needs_selection') {
      const selected = await this.openExistSelectionDialog(product, response.candidates);
      if (!selected) return;
      return this.runExistImport(product, selected.exist_product_id);
    }

    this.updateProducts([response.product]);
    this.notificationService.notice(this.buildImportSummary(response));
  }

  private async openExistSelectionDialog(
    product: Product,
    candidates: ProductImportFromExistCandidate[]
  ) {
    const dialogRef = this.dialog.open(ExistImportSelectionDialogComponent, {
      width: '900px',
      maxWidth: '96vw',
      data: {
        article: product.article,
        candidates
      }
    });

    return firstValueFrom(dialogRef.afterClosed());
  }

  private buildImportSummary(
    result: Extract<ProductImportFromExistResult, { status: 'imported' }>
  ) {
    const report = result.report;
    const parts: string[] = [];

    if (report.updated_ua_fields.length) {
      parts.push(`обновлены поля: ${report.updated_ua_fields.join(', ')}`);
    }
    if (report.added_images > 0) {
      parts.push(`добавлено картинок: ${report.added_images}`);
    }
    if (report.created_analog_products > 0 || report.linked_analog_products > 0) {
      parts.push(
        `аналоги: ${report.created_analog_products} созд., ${report.linked_analog_products} связ.`
      );
    }
    if (report.created_oe_products > 0 || report.linked_oe_products > 0) {
      parts.push(`OE: ${report.created_oe_products} созд., ${report.linked_oe_products} связ.`);
    }
    if (report.updated_exist_link) {
      parts.push('ссылка Exist сохранена');
    }
    if (report.skipped_duplicates > 0) {
      parts.push(`дубликаты пропущены: ${report.skipped_duplicates}`);
    }

    return parts.length
      ? `Импорт с Exist завершен: ${parts.join(' · ')}`
      : 'Импорт с Exist завершен без изменений';
  }

  updateName(product: Product) {
    const dialog = this.dialog.open(NameUpdateComponent);
    dialog.componentInstance.product = product;
    dialog.componentInstance.doneEvent.subscribe((data) => {
      if (data) dialog.close();
    });
  }

  quickPrintLabel(product: Product) {
    const dialogRef = this.dialog.open(LabelPrintComponent, { width: '480px', maxWidth: 'calc(100vw - 24px)' });
    dialogRef.componentInstance.productId = product.id;
    dialogRef.componentInstance.productTitle = `${product.brand?.description || ''} ${product.article || ''}`.trim();
    dialogRef.componentInstance.warehouseId = this.user?.defaultWarehouse?.id;
    dialogRef.componentInstance.barcode = product.barcodes?.find((row) => String(row?.barcode || '').trim())?.barcode || '';
  }

  editProductInWarehouse(product: Product) {
    const row = product.stock.find((_row) => {
      return _row.warehouse.id == this.user?.defaultWarehouse.id;
    });
    if (!row) return this.notificationService.warning('Не нашел продукт');
    const dialogRef = this.dialog.open(EditStockComponent);
    dialogRef.componentInstance.row = row;
    dialogRef.componentInstance.doneEvent.subscribe((value) => {
      if (value) {
        return dialogRef.close();
      }
    });
  }

  addBarcode(product: Product) {
    const dialogRef = this.dialog.open(AddToProductComponent);
    dialogRef.componentInstance.product = product;
    dialogRef.componentInstance.doneEvent.subscribe(() => {
      dialogRef.close();
    });
  }

  openFolders(product: Product) {
    const dialogRef = this.dialog.open(FoldersComponent);
    dialogRef.componentInstance.product = product;
  }

  openProps(product: Product) {
    const dialogRef = this.dialog.open(PropertiesComponent);
    dialogRef.componentInstance.product = product;
  }

  uploadFile() {
    const dialogRef = this.dialog.open(UploadComponent);
    dialogRef.componentInstance.doneEvent.subscribe(() => {
      dialogRef.close();
    });
  }

  setNewPrice(product: Product) {
    const dialogRef = this.dialog.open(SetNewPriceComponent);
    dialogRef.componentInstance.product = product;
    const subs = dialogRef.componentInstance.saveEvent.subscribe((value) => {
      if (value) dialogRef.close();
      subs.unsubscribe();
    });
  }

  openMoveProd(product: Product) {
    const dialogRef = this.dialog.open(MoveComponent, {
      width: '1500px',
      height: '800px'
    });
    dialogRef.componentInstance.product_id = product.id;
  }

  productInCart(id: number) {
    const index = this._cartProducts?.findIndex((prod) => prod.product.id === id);
    return index > -1;
  }

  getProductInCartGroup(product_id: number) {
    return this.cartProductsFrom.controls.find((ctrl) => {
      return ctrl.value.product.id === product_id;
    }) as FormGroup;
  }

  getProductCartControl(product_id: number) {
    const prodCtrl = this.cartProductsFrom.controls.find((ctrl) => {
      return ctrl.value.product.id === product_id;
    }) as FormGroup;
    if (!prodCtrl) return new FormControl();
    return prodCtrl.controls.quan as FormControl;
  }

  changeCartQuan(product: Product, event: any) {
    if (Number(event.target.value) === 0) {
      return this.removeFromCart(product);
    } else {
      this.store.dispatch(
        updateProductInCart({
          payload: product,
          quan: Number(event.target.value)
        })
      );
    }
  }

  removeFromCart(product: Product) {
    this.store.dispatch(removeFromCart({ payload: product }));
  }

  addToCart(product: Product) {
    this.store.dispatch(addToCart({ payload: product }));
  }

  sortData(event: any) {
    this.sortEvent.emit(event);
  }

  async openAnalogies(product: Product) {
    const ref = this.dialog.open(AnalogueListComponent, {
      height: 'calc(100% - 48px)'
    });
    ref.componentInstance.product = product;
  }

  getCol() {
    return this.products.length + ' поз.';
  }

  getRetailControlSum() {
    if (!this.productsFormArray) return;
    const sum = this.productsFormArray.controls.reduce((a, b) => {
      return a + b.value.retail * b.value.quan;
    }, 0);
    return this.supportService.formatMoney(sum);
  }

  getColSum() {
    if (!this.productsFormArray) return;
    const sum = this.productsFormArray.controls.reduce((a, b) => {
      return a + b.value.quan;
    }, 0);
    return sum + ' сум. поз.';
  }

  focusNext(event: KeyboardEvent, index: number, selector: string) {
    if (event.key === 'Enter') {
      let newIndex = 0;
      if (event.shiftKey) {
        newIndex = index;
      } else {
        newIndex = index + 2;
      }

      if (
        this.table?._elementRef.nativeElement.rows[newIndex] &&
        this.table?._elementRef?.nativeElement?.rows[newIndex].querySelector(
          `input[name="${selector}"]`
        )
      )
        this.table?._elementRef?.nativeElement?.rows[newIndex]
          .querySelector(`input[name="${selector}"]`)
          .focus();
    }
  }

  getQuickPrintMenuLabel() {
    const profile = this.user?.defaultLabelProfile;
    if (!profile) {
      return 'Настроить профиль этикеток';
    }

    const profileLabel = this.getLabelProfileDisplayLabel(profile);
    const prefix = this.isCompactLabelProfile(profile)
      ? 'Распечатать этикетку'
      : 'Распечатать ценник';
    return profileLabel ? `${prefix} (${profileLabel})` : prefix;
  }

  private isCompactLabelProfile(profile: LabelProfileRef) {
    const width = Number(profile.widthMm || 0);
    if (width > 0) {
      return width <= 35;
    }

    const profileCode = String(profile.profileCode || profile.code || '').trim();
    return profileCode.includes('30x20') || profileCode.includes('30x20'.toUpperCase());
  }

  private getLabelProfileDisplayLabel(profile: LabelProfileRef) {
    const width = Number(profile.widthMm || 0);
    const height = Number(profile.heightMm || 0);
    if (width > 0 && height > 0) {
      return `${width}x${height}`.replace(/\.0\b/g, '');
    }

    return String(
      profile.name || profile.profileName || profile.profileCode || profile.code || ''
    ).trim();
  }

  isPromLoading(id: number) {
    const index = this.loadingProm.findIndex((row) => row.id === id);
    return index !== -1;
  }

  isAvtoproLoading(id: number) {
    const index = this.loadingAvtopro.findIndex((row) => row.id === id);
    return index !== -1;
  }

  isPublicLoading(id: number) {
    const index = this.loadingPublic.findIndex((row) => row.id === id);
    return index !== -1;
  }

  isUpdateRetailLoading(id: number) {
    const index = this.loadingUpdateRetail.findIndex((row) => row.id === id);
    return index !== -1;
  }

  async toggleProm(id: number) {
    this.loadingProm.push({ id });
    await firstValueFrom(this.productService.toggleProm(id));
    const index = this.loadingProm.findIndex((row) => row.id === id);
    this.loadingProm.splice(index, 1);
  }

  async toggleAvtopro(id: number) {
    this.loadingAvtopro.push({ id });
    await firstValueFrom(this.productService.toggleAvtopro(id));
    const index = this.loadingAvtopro.findIndex((row) => row.id === id);
    this.loadingAvtopro.splice(index, 1);
  }

  async togglePublic(id: number) {
    this.loadingPublic.push({ id });
    await firstValueFrom(this.productService.togglePublic(id));
    const index = this.loadingPublic.findIndex((row) => row.id === id);
    this.loadingPublic.splice(index, 1);
  }

  async toggleUpdateRetail(product: Product) {
    if (this.isUpdateRetailLoading(product.id)) return;

    this.loadingUpdateRetail.push({ id: product.id });
    try {
      const updatedProduct = await firstValueFrom(
        this.productService.updateAutoRetail(product.id, !product.updateRetail)
      );
      product.updateRetail = Boolean(updatedProduct.updateRetail);
      this.updateProducts([updatedProduct]);
      this.notificationService.notice(
        product.updateRetail ? 'Автообновление цены включено' : 'Автообновление цены выключено'
      );
    } catch (err) {
      this.notificationService.warning('Не удалось изменить автообновление цены');
    } finally {
      const index = this.loadingUpdateRetail.findIndex((row) => row.id === product.id);
      if (index !== -1) this.loadingUpdateRetail.splice(index, 1);
    }
  }
}
